'use client';

// ============================================================================
// LA PORTE DE L'ECOSYSTEME : l'ecran de connexion
// ----------------------------------------------------------------------------
// REFONTE DU 27/09/2026. La demande du dirigeant, dans ses mots : « je veux
// vraiment qu'on m'impressionne », « le V de Vyndra qui possede les couleurs de
// tous les hubs, c'est ce logo-la qui est a conserver », « bienvenue dans
// l'univers de l'ecosysteme, vers ou tu veux te rendre », et « sur un ecran
// 27 pouces en 4K, qu'on vienne me chercher sur la largeur de l'ecran, qu'on
// arrete de me faire un petit menu au centre ».
//
// CE QUE LA PAGE EST DEVENUE.
//   - Plus de carte au centre. Sur ordinateur, la page reprend la forme du
//     logo : le titre coiffe trois parties, le formulaire a gauche (pose a meme
//     le fond), le logo Vyndra en monument au centre, repeint aux couleurs des
//     univers (prisme-ecosysteme.tsx), et les portes de chaque univers a droite
//     (portes-univers.tsx). Viser une porte allume ses losanges dans le logo.
//   - Toute la composition est a l'echelle de l'ecran (connexion.module.css,
//     unite --u) : 1440, 2560 et 3840 px de large ont la meme mise en page.
//   - Au telephone, une colonne : le prisme, le titre, le formulaire, puis les
//     portes. Le clavier ouvert ne cache rien, la page defile.
//
// SUR L'ADRESSE D'UN UNIVERS (guardinformatique.vyndra.fr), la meme page prend
// l'identite de cet univers : sa palette en variables CSS (variablesEspace),
// son logo en tete, son sceau eclaire a droite (sceau-univers.tsx), et AUCUNE
// autre porte (un salarie d'un seul univers ne doit pas voir les autres). Ces
// donnees viennent de `GET /hubs/public/by-host`, resolu par le composant
// serveur (page.tsx) pour que la premiere image soit deja la bonne. Rien n'est
// ecrit en dur.
//
// LE FONCTIONNEMENT N'A PAS CHANGE, et c'est une exigence : mot de passe, code
// par courriel (/api/auth/request-code), les deux cases (30 jours, appareil de
// confiance 7 jours), compte gele (423 vers /account-frozen), `?redirect=`,
// `?reason=expired`, messages d'erreur du serveur relayes tels quels, et la
// destination decidee par l'hote apres connexion (destinationApresConnexion).
// Corrections en plus : la redirection n'accepte plus qu'un chemin de
// l'application (redirectionSure), les champs declarent enfin leur role au
// gestionnaire de mots de passe (autocomplete), le bouton principal n'est plus
// grise mais dit ce qui manque, et les portes des univers menent a une adresse
// qui existe (l'ancien lien s'arretait a l'etiquette du sous-domaine).
// ============================================================================

import {
  ArrowRight,
  CircleNotch,
  Coffee,
  Clock,
  ShieldCheck,
  EnvelopeSimple,
  LockSimple,
  Eye,
  EyeSlash,
  Check,
  WarningCircle,
  ArrowFatUp,
} from '@phosphor-icons/react';
import { Suspense, useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent, type KeyboardEvent } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { VyndraLogo } from '@/components/vyndra-logo';
import { BrandLogo, lireReglagesEmplacements } from '@/components/ui/brand-asset';
import { construirePalette, variablesEspace } from '@/components/espace/couleurs';
import { EspacePwaInstall } from '@/components/espace/espace-pwa-install';
import { destinationApresConnexion } from '@/lib/hote-espace';
import { PrismeEcosysteme } from './prisme-ecosysteme';
import { SceauUnivers } from './sceau-univers';
import { ArriveePorte, PortesUnivers, type PorteUnivers } from './portes-univers';
import { colorerPrisme, redirectionSure, adresseRenforcement } from './ecosysteme';
import s from './connexion.module.css';

/**
 * L'identite publique de l'univers qui sert cette adresse, resolue par le
 * composant serveur. Nulle sur app.vyndra.fr : c'est alors la porte commune.
 */
export interface IdentitePublique {
  slug: string;
  /** Libelle de l'espace (« Hub Informatique »), mot d'organisation interne. */
  label: string;
  /**
   * Nom de la MARQUE (« Guard Informatique »), venu de l'identite visuelle
   * rattachee. C'est lui qu'on affiche : le dirigeant l'a demande
   * explicitement, et non le libelle du hub. Nul sans identite rattachee.
   */
  nomMarque: string | null;
  phrase: string | null;
  couleur: string | null;
  couleurSecondaire: string | null;
  /** Les quatre variantes brutes : la regle de repli commune a besoin des quatre. */
  logo: string | null;
  logoSombre: string | null;
  logoCarre: string | null;
  logoCarreSombre: string | null;
  /** Le choix du logo par emplacement, decode par le serveur (objet brut). */
  logoEmplacements: unknown;
}

// Phrases du bandeau « session expiree ». Une au hasard a chaque affichage,
// pour eviter la lassitude.
const PHRASES_EXPIRATION = [
  { icone: Coffee, message: 'Vyndra a fait une pause café un peu longue. Reconnectez-vous, votre bureau vous attend.' },
  { icone: Clock, message: 'Le temps passe vite quand on travaille bien. Votre session a pris un peu de vacances.' },
  { icone: ShieldCheck, message: 'Pour votre sécurité, on vous a déconnecté. Une connexion et c’est reparti.' },
  { icone: Coffee, message: 'Vous avez laissé Vyndra seul un bon moment. On ne vous en veut pas, reconnectez-vous.' },
];

interface Proprietes {
  espace: IdentitePublique | null;
  /** Les portes des univers : porte commune seulement, lecture reussie seulement. */
  portes: PorteUnivers[];
  /** L'hote de la requete, affiche sous la porte « Vyndra ». */
  hoteCourant: string | null;
  /** Version de l'application, lue dans package.json par le serveur. */
  version: string | null;
  /** `?porte=` a l'arrivee sur l'adresse d'un univers, sinon null. */
  gestePorte: string | null;
}

function ContenuConnexion({ espace, portes, hoteCourant, version, gestePorte }: Proprietes) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Deux options distinctes :
  //   - rememberMe : jeton de reprise de la duree maximale reglee par
  //     l'organisation (30 jours par defaut, au lieu de 24 h) ; la session se
  //     ferme plus tot sans aucun geste pendant le delai d'inactivite ;
  //   - trustDevice : temoin 7 jours, plus de code au prochain passage MEME
  //     apres une deconnexion manuelle.
  const [rememberMe, setRememberMe] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phrase, setPhrase] = useState<(typeof PHRASES_EXPIRATION)[number] | null>(null);
  // Voie « recevoir un code » : une fois empruntee, on masque la case appareil
  // de confiance, qui ne s'applique qu'au parcours par mot de passe.
  const [voieCode, setVoieCode] = useState(false);
  const [envoiCode, setEnvoiCode] = useState(false);
  const [voirMotDePasse, setVoirMotDePasse] = useState(false);
  const [majuscules, setMajuscules] = useState(false);
  const [aideOubli, setAideOubli] = useState(false);
  // L'univers vise par une porte : le prisme allume ses losanges.
  const [vise, setVise] = useState<string | null>(null);
  const champCourriel = useRef<HTMLInputElement>(null);
  const champMotDePasse = useRef<HTMLInputElement>(null);

  const reason = searchParams.get('reason');
  // Seul un CHEMIN de l'application est accepte (voir redirectionSure).
  const redirectAfter = redirectionSure(searchParams.get('redirect'));
  const isExpired = reason === 'expired';

  // La palette de l'univers. Hors univers, construirePalette retombe seule sur
  // l'accent applicatif, que la porte commune n'utilise pas pour son action.
  const palette = useMemo(
    () => construirePalette(espace?.couleur ?? null, espace?.couleurSecondaire ?? null),
    [espace?.couleur, espace?.couleurSecondaire],
  );
  const variables = useMemo(() => variablesEspace(palette), [palette]);

  // Le prisme, repeint aux couleurs des univers. Pur : meme resultat au rendu
  // serveur et dans le navigateur.
  const prisme = useMemo(
    () => colorerPrisme(portes.map((p) => ({ slug: p.slug, couleur: p.couleur }))),
    [portes],
  );

  /**
   * Ou l'on va apres une connexion reussie, decide par l'HOTE d'arrivee. Calcule
   * apres le montage : l'hote n'existe pas pendant le rendu serveur.
   */
  const [destination, setDestination] = useState('/lancer');
  useEffect(() => {
    setDestination(destinationApresConnexion(window.location.host));
  }, []);

  useEffect(() => {
    if (isExpired) {
      setPhrase(PHRASES_EXPIRATION[Math.floor(Math.random() * PHRASES_EXPIRATION.length)]);
    }
    // Pre-remplir l'adresse depuis le navigateur (accelere la reconnexion).
    let derniere: string | null = null;
    try {
      derniere = localStorage.getItem('vyndra_last_email');
      if (derniere) setEmail(derniere);
    } catch { /* stockage indisponible : on saisit simplement l'adresse */ }
    // LE CURSEUR SE POSE SEUL SUR ORDINATEUR, JAMAIS AU TELEPHONE : sur un ecran
    // tactile, le clavier surgirait des l'ouverture et cacherait la page avant
    // meme qu'on l'ait vue.
    if (window.matchMedia?.('(pointer: fine)').matches) {
      (isExpired && derniere ? champMotDePasse : champCourriel).current?.focus();
    }
  }, [isExpired]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (loading || envoiCode) return;
    // LE BOUTON RESTE ALLUME, ET DIT CE QUI MANQUE. Il etait grise tant que les
    // deux champs n'etaient pas remplis : l'action principale de la page se
    // lisait comme indisponible, sans dire pourquoi. On guide au lieu de griser.
    if (!email.trim()) {
      setError('Saisissez votre adresse courriel.');
      champCourriel.current?.focus();
      return;
    }
    if (!password) {
      setError('Saisissez votre mot de passe, ou recevez un code par courriel juste en dessous.');
      champMotDePasse.current?.focus();
      return;
    }
    setError(null);
    setVoieCode(false);
    setLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, rememberMe, trustDevice }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        // 423 ACCOUNT_FROZEN : page dediee plutot qu'un message generique, sinon
        // la personne recommence indefiniment a saisir son mot de passe.
        if (res.status === 423 && data?.error === 'ACCOUNT_FROZEN') {
          const url = new URL('/account-frozen', window.location.origin);
          if (data.reason) url.searchParams.set('reason', String(data.reason));
          router.replace(url.pathname + url.search);
          return;
        }
        setError(data?.error || 'Connexion impossible pour le moment. Réessayez dans un instant.');
        setLoading(false);
        return;
      }
      if (!data) {
        setError('Réponse illisible du serveur. Réessayez dans un instant.');
        setLoading(false);
        return;
      }

      try { localStorage.setItem('vyndra_last_email', email); } catch { /* stockage indisponible */ }

      // Cas 1 : appareil de confiance reconnu, entree directe sans code. Le
      // relais a deja pose les cookies de session et de reprise, sur le domaine
      // partage par tous les hotes. Destination : celle que le SERVEUR a
      // calculee depuis l'hote, sinon celle calculee ici.
      if (data.trusted === true) {
        const arrivee = redirectAfter || data.redirect || destination;
        // Mot de passe a renforcer (compte de direction, revue du 27/09/2026, A4) :
        // proposition AVANT la destination, jamais un blocage.
        router.push(
          data.motDePasseARenforcer === true
            ? adresseRenforcement(arrivee, window.location.origin)
            : arrivee,
        );
        return;
      }

      // Cas 2 : code requis, vers /verify avec les options propagees. L'ecran du
      // code vit sur le MEME hote, il decidera donc de la meme destination.
      const params = new URLSearchParams();
      if (rememberMe) params.set('remember', '1');
      if (trustDevice) params.set('trust', '1');
      if (redirectAfter) params.set('redirect', redirectAfter);
      if (data.motDePasseARenforcer === true) params.set('renforcer', '1');
      const qs = params.toString();
      router.push(qs ? `/verify?${qs}` : '/verify');
    } catch {
      setError('Erreur réseau. Vérifiez votre connexion puis réessayez.');
      setLoading(false);
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // LA VOIE PAR CODE. /api/auth/request-code ne demande QUE l'adresse, et ne dit
  // JAMAIS si le compte existe : le serveur repond la meme chose pour une
  // adresse connue, inconnue, gelee ou protegee par une application
  // d'authentification. C'est ce qui empeche cet ecran de devenir un annuaire
  // du personnel. On avance donc toujours vers /verify.
  // ══════════════════════════════════════════════════════════════════════════
  const demanderCodeParCourriel = async () => {
    if (loading || envoiCode) return;
    if (!email.trim()) {
      setError('Saisissez votre adresse courriel pour recevoir un code.');
      champCourriel.current?.focus();
      return;
    }
    setError(null);
    setVoieCode(true);
    // L'appareil de confiance appartient au parcours par mot de passe : on ne le
    // demande pas ici et on ne le propage pas a /verify.
    setTrustDevice(false);
    setEnvoiCode(true);
    try {
      const res = await fetch('/api/auth/request-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        // Les seuls refus possibles sont les plafonds de debit, qui ne disent
        // rien de l'adresse. On relaie le message du serveur tel quel.
        setError(data.error || "Impossible d'envoyer un code pour le moment.");
        setEnvoiCode(false);
        return;
      }

      try { localStorage.setItem('vyndra_last_email', email); } catch { /* stockage indisponible */ }

      const params = new URLSearchParams();
      if (rememberMe) params.set('remember', '1');
      if (redirectAfter) params.set('redirect', redirectAfter);
      // La voie par code : l'ecran suivant ne promet pas un code a coup sur, un compte
      // de direction recoit a la place la marche a suivre (revue du 27/09/2026, A4).
      params.set('voie', 'code');
      const qs = params.toString();
      router.push(qs ? `/verify?${qs}` : '/verify');
    } catch {
      setError('Erreur réseau. Vérifiez votre connexion puis réessayez.');
      setEnvoiCode(false);
    }
  };

  const suivreMajuscules = (e: KeyboardEvent<HTMLInputElement>) => {
    if (typeof e.getModifierState === 'function') setMajuscules(e.getModifierState('CapsLock'));
  };

  // Le nom affiche est celui de la MARQUE, pas le libelle interne de l'espace
  // (« Hub Informatique »). Repli sur le libelle quand aucune identite visuelle
  // n'est rattachee, jamais un decoupage de chaine.
  const nomMarque = espace?.nomMarque?.trim() || espace?.label || null;
  const jeuLogos = espace
    ? {
        logoUrl: espace.logo,
        logoDarkUrl: espace.logoSombre,
        logoSquareUrl: espace.logoCarre,
        logoSquareDarkUrl: espace.logoCarreSombre,
      }
    : null;
  const reglagesEmplacements = lireReglagesEmplacements(espace?.logoEmplacements);

  // L'univers d'arrivee, decrit comme une porte : les battants et le sceau
  // lisent ses couleurs et son logo.
  const porteArrivee: PorteUnivers | null = espace && jeuLogos
    ? {
        slug: espace.slug,
        nom: nomMarque ?? espace.label,
        couleur: espace.couleur,
        couleurSecondaire: espace.couleurSecondaire,
        jeu: jeuLogos,
        logoEmplacements: espace.logoEmplacements,
        href: '/',
        affichee: '',
      }
    : null;

  // Espaces insecables avant « : » et « ? », comme le veut la typographie
  // francaise : sans eux, le signe peut partir seul en debut de ligne.
  const chapo = isExpired
    ? 'Votre session a expiré. Reconnectez-vous pour reprendre là où vous en étiez.'
    : espace
      ? espace.phrase?.trim() || 'Identifiez-vous pour entrer dans votre espace.'
      : (
          <>
            Un seul compte pour Vyndra et tous vos univers&nbsp;:{' '}
            <span className="whitespace-nowrap">identifiez-vous</span>, puis choisissez où travailler.
          </>
        );

  const IconePhrase = phrase?.icone ?? Coffee;

  return (
    <div
      // Les variables de la palette sont posees ICI : tout le decor les lit, y
      // compris l'action principale, qui prend la couleur de la marque dans un
      // univers et celle de la clef de voute du logo sur la porte commune.
      style={variables}
      data-espace={espace?.slug ?? undefined}
      data-mode={espace ? 'univers' : 'ecosysteme'}
      // Trois parties sur la porte commune (formulaire, logo, portes), deux sur
      // l'adresse d'un univers (formulaire, sceau de la marque).
      data-disposition={espace ? 'duo' : 'triptyque'}
      className={s.scene}
    >
      <div className={s.decor} aria-hidden="true" />

      <header className={`${s.tete} ${s.monte}`} style={{ '--d': '0s' } as CSSProperties}>
        {espace && jeuLogos ? (
          // Le logo de la marque, choisi par le dirigeant pour l'emplacement
          // « connexion ». Le fichier porte deja le nom : on ne l'ecrit pas a
          // cote (constat du 08/08/2026 : « le double logo bidon »).
          <BrandLogo
            jeu={jeuLogos}
            emplacement="connexion"
            reglagesEmplacements={reglagesEmplacements}
            nom={nomMarque ?? espace.label}
            className={s.marqueLogoUnivers}
          />
        ) : (
          <span className={s.marque}>
            {/* Au telephone, le prisme ouvre la page juste dessous : le petit
                logo de tete ferait doublon, il n'apparait que sur ordinateur. */}
            <VyndraLogo size={30} className={s.marqueLogo} aria-hidden="true" />
            <span className={s.marqueNom}>Vyndra</span>
          </span>
        )}
      </header>

      <main className={s.corps}>
        <div className={s.titrage}>
          {/* Au telephone, le prisme ouvre la page, au-dessus du titre. Sur
              ordinateur, il vit en monument au centre du triptyque. */}
          {!espace && (
            <div className={s.prismeMobile}>
              <PrismeEcosysteme prisme={prisme} vise={null} />
            </div>
          )}

          <h1 className={`${s.titre} ${s.monte}`} style={{ '--d': '0.05s' } as CSSProperties}>
            <span className={`${s.titreLigne} ${s.titreDoux}`}>Bienvenue dans</span>{' '}
            <span className={s.titreLigne}>
              {espace ? 'votre espace' : 'l’écosystème Vyndra'}
              <span className={s.clef} aria-hidden="true" />
              <span className={s.masque}>.</span>
            </span>
          </h1>
          <p className={`${s.chapo} ${s.monte}`} style={{ '--d': '0.1s' } as CSSProperties}>
            {chapo}
          </p>

          {isExpired && phrase && (
            <div className={`${s.bandeau} ${s.monte}`} role="status" style={{ '--d': '0.12s' } as CSSProperties}>
              <IconePhrase size={18} weight="duotone" aria-hidden="true" className={`${s.ico} mt-[1px]`} />
              <span>
                <span className={s.bandeauTitre}>Session expirée</span>
                <span className={s.bandeauTexte}>{phrase.message}</span>
              </span>
            </div>
          )}
        </div>

        <div className={`${s.colonne} ${s.monte}`} style={{ '--d': '0.16s' } as CSSProperties}>
          <form onSubmit={handleSubmit} className={s.formulaire} noValidate>
            <div className={s.champ}>
              <label htmlFor="connexion-courriel" className={s.libelle}>Adresse courriel</label>
              <div className={s.boite}>
                <EnvelopeSimple size={18} className={`${s.ico} ${s.boiteIcone}`} aria-hidden="true" />
                <input
                  ref={champCourriel}
                  id="connexion-courriel"
                  name="email"
                  type="email"
                  inputMode="email"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="vous@entreprise.fr"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    // La personne corrige : le message qui la visait n'a plus lieu d'etre.
                    if (error) setError(null);
                  }}
                  className={s.entree}
                />
              </div>
            </div>

            <div className={s.champ}>
              <div className={s.champEntete}>
                <label htmlFor="connexion-mot-de-passe" className={s.libelle}>Mot de passe</label>
                <button
                  type="button"
                  className={s.lienDiscret}
                  aria-expanded={aideOubli}
                  aria-controls="connexion-aide-oubli"
                  onClick={() => setAideOubli((v) => !v)}
                >
                  Mot de passe oublié&nbsp;?
                </button>
              </div>
              <div className={s.boite}>
                <LockSimple size={18} className={`${s.ico} ${s.boiteIcone}`} aria-hidden="true" />
                <input
                  ref={champMotDePasse}
                  id="connexion-mot-de-passe"
                  name="password"
                  type={voirMotDePasse ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Votre mot de passe"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) setError(null);
                    // La personne revient au mot de passe : on ressort de la voie par code.
                    if (voieCode) setVoieCode(false);
                  }}
                  onKeyDown={suivreMajuscules}
                  onKeyUp={suivreMajuscules}
                  onBlur={() => setMajuscules(false)}
                  className={`${s.entree} ${s.entreeAvecBouton}`}
                />
                <button
                  type="button"
                  className={s.boutonOeil}
                  aria-label={voirMotDePasse ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  aria-pressed={voirMotDePasse}
                  onClick={() => setVoirMotDePasse((v) => !v)}
                >
                  {voirMotDePasse
                    ? <EyeSlash size={18} weight="duotone" className={s.ico} aria-hidden="true" />
                    : <Eye size={18} weight="duotone" className={s.ico} aria-hidden="true" />}
                </button>
              </div>
              {majuscules && (
                <p className={s.aideAttention} role="status">
                  <ArrowFatUp size={14} weight="fill" aria-hidden="true" />
                  Les majuscules sont activées.
                </p>
              )}
              {/* Deux issues reelles, et seulement des issues reelles : le code
                  par courriel juste en dessous, ou un lien de reinitialisation
                  que la personne qui gere les acces envoie depuis la fiche du
                  membre. Il n'existe pas de reinitialisation qu'on declenche
                  soi-meme : un lien ici ne menerait nulle part. */}
              {aideOubli && (
                <div id="connexion-aide-oubli" className={s.volet}>
                  <p className={s.aide}>
                    Recevez un code par courriel juste en dessous&nbsp;: il remplace le mot de passe
                    pour cette connexion. Pour un compte Propriétaire ou Administrateur, ce
                    courriel contient à la place un lien pour choisir un nouveau mot de passe.
                    Les autres comptes demandent ce lien à la personne qui gère les accès.
                  </p>
                </div>
              )}
            </div>

            <div className={s.cases}>
              <label className={s.case}>
                <input
                  type="checkbox"
                  className={s.caseEntree}
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span className={s.caseBoite} aria-hidden="true">
                  <Check size={13} weight="bold" />
                </span>
                <span className={s.caseTexte}>
                  {/* La durée n'est plus écrite en dur (30/09/2026) : elle se règle par
                      organisation (Réglages > Sécurité), et une session sans aucun geste
                      pendant plusieurs jours se ferme d'elle-même. */}
                  <span className={s.caseTitre}>Maintenir ma session active</span>
                  <span className={s.caseAide}>Jusqu’à la durée réglée par votre organisation (30&nbsp;jours par défaut), tant que vous vous en servez.</span>
                </span>
              </label>

              {/* Case masquee sur la voie par code : le serveur refuse ce
                  raccourci pour les comptes sans mot de passe. */}
              {voieCode ? (
                <div className={s.caseNote}>
                  <EnvelopeSimple size={16} aria-hidden="true" className={`${s.ico} mt-[2px]`} />
                  <span>Vous vous connectez avec un code reçu par courriel. Un code est demandé à chaque connexion.</span>
                </div>
              ) : (
                <label className={s.case}>
                  <input
                    type="checkbox"
                    className={s.caseEntree}
                    checked={trustDevice}
                    onChange={(e) => setTrustDevice(e.target.checked)}
                  />
                  <span className={s.caseBoite} aria-hidden="true">
                    <Check size={13} weight="bold" />
                  </span>
                  <span className={s.caseTexte}>
                    <span className={s.caseTitre}>
                      Faire confiance à cet appareil <strong>7&nbsp;jours</strong>
                    </span>
                    <span className={s.caseAide}>Plus de code demandé, même après une déconnexion. Sur un appareil personnel uniquement.</span>
                  </span>
                </label>
              )}
            </div>

            {error && (
              <div className={s.erreur} role="alert">
                <WarningCircle size={18} weight="duotone" aria-hidden="true" className={`${s.ico} mt-[1px]`} />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              className={`${s.bouton} ${s.boutonPrincipal}`}
              disabled={loading || envoiCode}
              aria-busy={loading || undefined}
            >
              {loading ? (
                <>
                  <CircleNotch size={18} className={`${s.ico} ${s.tourne}`} aria-hidden="true" />
                  Connexion…
                </>
              ) : (
                <>
                  {isExpired ? 'Me reconnecter' : 'Se connecter'}
                  <ArrowRight size={18} weight="bold" className={`${s.ico} ${s.boutonFleche}`} aria-hidden="true" />
                </>
              )}
            </button>

            <div className={s.ou} aria-hidden="true">ou</div>

            {/* Voie sans mot de passe. Elle ne depend QUE de l'adresse : les
                salaries n'ont pas de mot de passe a saisir. */}
            <div className={s.codeBloc}>
              <button
                type="button"
                className={`${s.bouton} ${s.boutonSecondaire}`}
                onClick={demanderCodeParCourriel}
                disabled={loading || envoiCode}
                aria-busy={envoiCode || undefined}
              >
                {envoiCode ? (
                  <>
                    <CircleNotch size={18} className={`${s.ico} ${s.tourne}`} aria-hidden="true" />
                    Envoi du code…
                  </>
                ) : (
                  <>
                    <EnvelopeSimple size={18} className={s.ico} aria-hidden="true" />
                    Recevoir un code par courriel
                  </>
                )}
              </button>
              <p className={s.aide}>
                {!email
                  ? 'Saisissez votre adresse ci-dessus pour recevoir un code.'
                  : 'Un code à 6 chiffres part sur votre adresse. Le mot de passe n’est pas nécessaire.'}
              </p>
            </div>
          </form>
        </div>

        <div className={s.centre}>
          {espace && jeuLogos ? (
            // Le sceau de la marque, en grand, a la place du prisme : sur
            // l'adresse d'un univers, c'est SA marque qui tient la scene.
            <SceauUnivers jeu={jeuLogos} logoEmplacements={espace.logoEmplacements} />
          ) : (
            <PrismeEcosysteme prisme={prisme} vise={vise} />
          )}
        </div>

        {!espace && portes.length > 0 && (
          <div className={`${s.droite} ${s.monte}`} style={{ '--d': '0.24s' } as CSSProperties}>
            <PortesUnivers portes={portes} hoteCourant={hoteCourant} onViser={setVise} />
          </div>
        )}
      </main>

      <footer className={s.pied}>
        {/* On ne cree pas de compte soi-meme : l'acces vient d'une invitation
            envoyee par l'entreprise. Un lien « Creer un compte » serait faux. */}
        <p className={s.piedInvitation}>
          Pas encore de compte&nbsp;? L’accès se fait sur invitation&nbsp;:{' '}
          <span className="whitespace-nowrap">demandez-la</span> à la personne qui gère les accès dans
          votre entreprise.
        </p>
        {/* Dans un univers, on ne signe pas « Vyndra Cloud » : le salarie n'a pas
            a savoir quel outil est derriere. On rappelle la marque ou il entre. */}
        <span className={s.piedVersion}>
          {espace
            ? `${nomMarque ?? espace.label} · Vyndra`
            : `Vyndra Cloud${version ? ` v${version}` : ''} · Made in France`}
        </span>
      </footer>

      {/* Installation de l'application sur l'adresse d'un univers
          (mockups/connexion-espaces.html), jamais sur app.vyndra.fr ou
          « installer » ne designe rien de precis. */}
      {espace && <EspacePwaInstall slug={espace.slug} nomEspace={nomMarque ?? espace.label} />}

      {porteArrivee && gestePorte && <ArriveePorte porte={porteArrivee} geste={gestePorte} />}
    </div>
  );
}

export function EcranConnexion(props: Proprietes) {
  return (
    <Suspense fallback={<div className="min-h-screen bg-[#07080A]" />}>
      <ContenuConnexion {...props} />
    </Suspense>
  );
}
